/** Dialog for requesting a dental for a patient: a note to the dental team, with no fee and no membership check. */
import { toast } from 'sonner'
import { z } from 'zod'

import type { PatientWithOwners } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { useCreateTask } from '@/frontend/features/tasks/use-create-task'

const dentalRequestSchema = z.object({
  note: z.string().trim().min(1, 'A note for the team is required').max(1000),
})

interface DentalRequestDialogProps {
  patient: PatientWithOwners
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DentalRequestDialog({ patient, open, onOpenChange }: DentalRequestDialogProps) {
  const { mutateAsync: createTask } = useCreateTask()

  const form = useAppForm({
    defaultValues: { note: '' },
    validators: { onSubmit: dentalRequestSchema },
    onSubmit: async ({ value, formApi }) => {
      const { note } = dentalRequestSchema.parse(value)
      try {
        await createTask({
          patientId: patient.id,
          data: { type: 'procedure_request', procedure: 'dental', note, urgent: false },
        })
        toast.success('Sent to the team')
        form.reset()
        onOpenChange(false)
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to send dental request',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request dental — {patient.name}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <form.AppForm>
            <div className="flex flex-col gap-4 py-2">
              <form.GlobalError />

              <form.AppField name="note">
                {(field) => (
                  <field.TextareaField
                    label="Note"
                    required
                    placeholder="e.g. Grade 3 dental disease, owner keen to book…"
                    maxLength={1000}
                    autoFocus
                  />
                )}
              </form.AppField>
            </div>

            <form.Subscribe selector={(s) => [s.isPristine, s.isSubmitting] as const}>
              {([isPristine, isSubmitting]) => (
                <div className="flex items-center justify-end gap-2 pt-3">
                  <Button
                    variant="outline"
                    size="lg"
                    type="button"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton disabled={isPristine}>Send request</form.SubmitButton>
                </div>
              )}
            </form.Subscribe>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  )
}
